import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} TextTruncateRefs
 * @property {HTMLElement} truncateContent - The content wrapper to clamp and animate
 * @property {HTMLButtonElement} truncateButton - The toggle button
 */

/**
 * Truncates text to a configurable number of lines with an animated expand/collapse toggle.
 * Configured via data-lines-desktop and data-lines-mobile attributes.
 *
 * @extends {Component<TextTruncateRefs>}
 */
class TextTruncateComponent extends Component {
  requiredRefs = ['truncateContent', 'truncateButton'];

  #expanded = false;
  #collapsedHeight = 0;
  #animation;
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
  }

  get #linesDesktop() {
    return parseInt(this.dataset.linesDesktop) || 3;
  }

  get #linesMobile() {
    return parseInt(this.dataset.linesMobile) || this.#linesDesktop;
  }

  get #lines() {
    return isMobileBreakpoint() ? this.#linesMobile : this.#linesDesktop;
  }

  #setup = () => {
    const { truncateContent, truncateButton } = this.refs;
    if (!truncateContent || !truncateButton) return;

    truncateContent.style.removeProperty('max-height');
    truncateContent.style.removeProperty('overflow');

    const lineHeight = parseFloat(getComputedStyle(truncateContent).lineHeight) || 24;
    this.#collapsedHeight = Math.round(lineHeight * this.#lines);

    if (truncateContent.scrollHeight <= this.#collapsedHeight + 2) {
      truncateButton.hidden = true;
      return;
    }

    truncateButton.hidden = false;
    truncateContent.style.maxHeight = `${this.#collapsedHeight}px`;
    truncateContent.style.overflow = 'hidden';
  };

  #onResize = () => {
    if (this.#expanded) return;
    this.#setup();
  };

  toggle = (event) => {
    event.preventDefault();
    const { truncateContent, truncateButton } = this.refs;
    const expanding = !this.#expanded;

    const startHeight = truncateContent.offsetHeight;
    let endHeight;

    if (expanding) {
      truncateContent.style.removeProperty('max-height');
      endHeight = truncateContent.scrollHeight;
    } else {
      const lineHeight = parseFloat(getComputedStyle(truncateContent).lineHeight) || 24;
      this.#collapsedHeight = Math.round(lineHeight * this.#lines);
      endHeight = this.#collapsedHeight;
    }

    this.dataset.expanded = expanding.toString();
    truncateButton.setAttribute('aria-expanded', expanding.toString());

    this.#animateHeight(truncateContent, startHeight, endHeight, expanding);
  };

  #animateHeight = (el, startHeight, endHeight, expanding) => {
    el.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = el.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      el.style.removeProperty('height');
      if (expanding) {
        el.style.removeProperty('max-height');
        el.style.removeProperty('overflow');
      } else {
        el.style.maxHeight = `${this.#collapsedHeight}px`;
        el.style.overflow = 'hidden';
      }
      this.#expanded = expanding;
    };
  };
}

if (!customElements.get('text-truncate-component')) {
  customElements.define('text-truncate-component', TextTruncateComponent);
}
